import React, { useState } from "react";

import {
  ArrayUtils,
  formatDayDate,
  formatNumber,
  getCropName,
  useGetFieldPractices,
  useInView,
  useTillageAndSeedingHelpers,
  useUpdateVerificationStatus,
} from "utils";

import { CropFCPO, FieldVerification, PRACTICE_SLUG, PracticeFCPO, VerificationStatus } from "models";

import { Col, Icon, Message, Row, SkeletonWrapper } from "components";

import { CropDrawer } from "pages/admin/verifications/components/misc/CropDrawer";
import { VerificationStatusDropdown } from "pages/admin/verifications/components/misc/VerificationStatusDropdown";

interface TillageAndSeedingTableProps {
  field: FieldVerification;
  farmId: string;
}

export const TillageAndSeedingTable = ({ field, farmId }: TillageAndSeedingTableProps) => {
  const [cropToEdit, setCropToEdit] = useState<CropFCPO | null>(null);

  const fieldId = field.id;
  const { getPracticeName, getDieselUsage, getCombinedPractices, sortPracticesByEndDate, getPracticeIconName } = useTillageAndSeedingHelpers();
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const { data: fieldPractices, isLoading } = useGetFieldPractices({ fieldId, inView, isAdmin: true });

  // Handler for clicking on a practice row to open the crop drawer
  const handlePracticeClick = (practice: PracticeFCPO) => {
    const associatedCrop = fieldPractices?.crops?.find((c) => c.id === practice.cropId);
    if (associatedCrop) {
      setCropToEdit(associatedCrop);
    }
  };

  const { mutate: updateStatus } = useUpdateVerificationStatus({ fieldId, farmId });

  // Handler for updating tillage/seeding verification status
  const handleStatusChange = (practiceId: string, practiceType: string, newStatus: VerificationStatus) => {
    if (!newStatus) return;

    updateStatus(
      {
        practiceType,
        practiceId,
        verificationStatus: newStatus,
      },
      {
        onSuccess: () => {
          Message({ icon: "tick", content: "Verification status updated successfully" });
        },
      }
    );
  };

  // Get combined tillage and seeding practices
  const practices = getCombinedPractices(fieldPractices);

  // Group practices by year
  const practicesByYear = ArrayUtils.groupBy(practices, (practice) => practice?.year ?? "unknown");

  // Sort years in descending order
  const sortedYears = Object.keys(practicesByYear || {})
    .filter((year) => year !== "unknown")
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <div ref={ref} className="crop-table">
      <SkeletonWrapper isLoading={isLoading} type="block" height="20rem">
        {/* Table Header */}
        <Row className="crop-table-header">
          <Col md={6} className="header-cell">
            practice name
          </Col>
          <Col md={3} className="header-cell">
            diesel usage
          </Col>
          <Col md={2} className="header-cell">
            tillage depth
          </Col>
          <Col md={4} className="header-cell">
            date range
          </Col>
          <Col md={5} className="header-cell">
            status
          </Col>
          <Col md={4} className="header-cell"></Col>
        </Row>

        {/* Table Body grouped by year */}
        {sortedYears.map((year) => {
          const yearPractices = practicesByYear[year];
          const sortedPractices = sortPracticesByEndDate([...yearPractices]);

          return (
            <div key={year} className="year-group">
              {/* Year header */}
              <Row className="year-header" align="middle">
                <Col md={6}>
                  <h4 className="year-title">{year}</h4>
                </Col>
                <Col md={3}></Col>
                <Col md={2}></Col>
                <Col md={4}></Col>
                <Col md={5}></Col>
                <Col md={4}></Col>
              </Row>

              {/* Practices for this year */}
              {sortedPractices.map((practice) => {
                const cropName = getCropName(practice.cropId, fieldPractices);
                const dieselUsage = getDieselUsage(practice);
                return (
                  <Row key={practice.id} className="crop-row pointer" onClick={() => handlePracticeClick(practice)}>
                    <Col md={6} className="crop-cell">
                      <div className="crop-name-cell">
                        <div className={`crop-indicator ${practice.verificationStatus}`} />
                        <Icon iconName={getPracticeIconName(practice.type)} fill="none" className="icon inline margin-right-50" size="s" />
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
                          <span className="paragraph-s-bold">{getPracticeName(practice.type, practice.assignedPracticeId)}</span>
                          {cropName && <span className="paragraph-xs grey-text">{cropName}</span>}
                        </div>
                      </div>
                    </Col>
                    <Col md={3} className="crop-cell">
                      <span className="paragraph-xs">
                        {dieselUsage !== null && dieselUsage !== undefined ? `${formatNumber({ number: dieselUsage, decimalPlaces: 2 })} L/ha` : "-"}
                      </span>
                    </Col>
                    <Col md={2} className="crop-cell">
                      <span className="paragraph-xs">
                        {practice.type === PRACTICE_SLUG.TILLAGES && practice.tillageDepth
                          ? `${formatNumber({ number: practice.tillageDepth, decimalPlaces: 1 })} cm`
                          : "-"}
                      </span>
                    </Col>
                    <Col md={4} className="crop-cell">
                      <span className="paragraph-xs">
                        {practice.startDate && practice.endDate
                          ? `${formatDayDate(practice.startDate)} - ${formatDayDate(practice.endDate)}`
                          : practice.endDate
                          ? formatDayDate(practice.endDate)
                          : "-"}
                      </span>
                    </Col>
                    <Col md={5} className="crop-cell">
                      <VerificationStatusDropdown
                        status={practice.verificationStatus ?? null}
                        cropId={practice.id!}
                        onStatusChange={(_, status) => {
                          // For seedings, use seedingPracticeId; for tillages, use practice.id
                          handleStatusChange(practice.seedingPracticeId ?? practice.id!, practice.type, status);
                        }}
                      />
                    </Col>
                    <Col md={4} className="crop-cell"></Col>
                  </Row>
                );
              })}
            </div>
          );
        })}

        {/* Empty state */}
        {sortedYears.length === 0 && !isLoading && (
          <Row className="empty-state">
            <Col md={24}>
              <span className="paragraph-s text-disabled">No tillage or seeding practices found for this field</span>
            </Col>
          </Row>
        )}
      </SkeletonWrapper>

      <CropDrawer
        crop={cropToEdit}
        showDrawer={!!cropToEdit}
        onClose={() => setCropToEdit(null)}
        fieldPractices={fieldPractices}
        field={field}
        farmId={farmId}
      />
    </div>
  );
};
